import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useQuotePrinter } from "@/hooks/useQuotePrinter";
import { useInvoiceSender } from "@/hooks/useInvoiceSender";
import { docFromInvoice } from "@/lib/quoteDoc";
import { quoteDocText } from "@/lib/quoteText";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;

// After a vehicle is marked collected: ask once, then WhatsApp the invoice PDF (with the PayNow QR) to the customer.
export function useCollectionSend(shop) {
  const printer = useQuotePrinter();
  const { sharePdf } = useInvoiceSender(printer, shop);
  const [pending, setPending] = useState(null); // quote-like record with invoice_id / customer_phone

  const promptSend = (q) => {
    if (!q?.invoice_id || q.status === "paid") return; // nothing to bill (monthly-billed fleet or already paid)
    if (!q.customer_phone) { toast.info("Collected — no phone on file to send the invoice"); return; }
    setPending(q);
  };

  const send = async () => {
    const q = pending; setPending(null);
    try {
      const { data: inv } = await axios.get(`${API}/invoices/${q.invoice_id}`);
      await sharePdf(inv, quoteDocText(docFromInvoice(inv, shop)));
    } catch (e) { toast.error("Could not open WhatsApp", { description: e?.response?.data?.detail || String(e?.message || e) }); }
  };

  const element = (
    <>
      {printer.portal}
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent className="bg-[#111827] border-slate-700 text-slate-100 max-w-md" data-testid="collection-send-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2"><MessageCircle className="w-5 h-5 text-emerald-400" /> Vehicle collected · 已交车</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Send <b className="text-slate-200">{pending?.customer_name || "the customer"}</b> the invoice {pending?.invoice_no ? <b className="text-sky-300 font-mono-num">{pending.invoice_no}</b> : null} with the PayNow QR via WhatsApp{pending?.customer_phone ? <> to <span className="font-mono-num text-slate-200">{pending.customer_phone}</span></> : null}?
              <span className="block mt-1 text-xs">通过 WhatsApp 发送发票和 PayNow 二维码给客户？</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="collection-send-no" className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-slate-100">Not now · 稍后</AlertDialogCancel>
            <AlertDialogAction onClick={send} data-testid="collection-send-yes" className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold"><MessageCircle className="w-4 h-4 mr-1.5" /> Yes, send via WhatsApp</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
  return { promptSend, element };
}
